/**
 * PricingRuleCreateWidgets — reusable presentational primitives for the
 * Pricing Rule + Coupon wizard. Composite sections live in
 * PricingRuleCreateSections.tsx; the orchestrator is PricingRuleCreatePage.tsx.
 */

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import {
  Check,
  Tag,
  Gift,
  Percent,
  Sparkles,
  Package,
  ArrowRight,
  Star,
} from "lucide-react";
import type { RewardKind, DiscountMode } from "./pricingRuleWizardModel";

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{children}</h2>;
}

// ── Reward picker (Discount vs Free item) ───────────────────

export function RewardPicker({ value, onChange, lang }: {
  value: RewardKind;
  onChange: (t: RewardKind) => void;
  lang: string;
}) {
  const items = [
    { type: "discount" as RewardKind, icon: Tag, label: { en: "Discount", th: "ส่วนลด" }, desc: { en: "% or ฿ off the price", th: "ลดราคาเป็น % หรือ ฿" } },
    { type: "product" as RewardKind, icon: Gift, label: { en: "Free item", th: "แถมสินค้า" }, desc: { en: "Buy X get Y free", th: "ซื้อ X แถม Y" } },
  ];
  return (
    <div className="grid grid-cols-2 gap-3">
      {items.map((item) => {
        const active = value === item.type;
        const Icon = item.icon;
        return (
          <button key={item.type} type="button" onClick={() => onChange(item.type)}
            className={cn(
              "relative rounded-md p-4 transition-all text-left bg-white dark:bg-card",
              active ? "shadow-sm ring-1 ring-primary/30" : "hover:shadow-sm border border-border hover:border-muted-foreground/30"
            )}>
            <div className="space-y-2.5">
              <div className={cn("w-9 h-9 rounded flex items-center justify-center transition-colors", active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <p className={cn("text-sm font-medium", active ? "text-primary" : "text-foreground")}>
                  {lang === "th" ? item.label.th : item.label.en}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  {lang === "th" ? item.desc.th : item.desc.en}
                </p>
              </div>
            </div>
            {active && (
              <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <Check className="w-3 h-3 text-primary-foreground" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Segmented control ───────────────────────────────────────

export function SegmentedControl<T extends string>({ options, value, onChange }: {
  options: { value: T; label: string; icon?: typeof Percent }[];
  value: T | null;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex rounded bg-muted/50 p-1 gap-0.5">
      {options.map((opt) => {
        const active = value === opt.value;
        const Icon = opt.icon;
        return (
          <button key={opt.value} type="button" onClick={() => onChange(opt.value)}
            className={cn(
              "flex items-center justify-center gap-1.5 rounded-sm px-4 py-2 text-sm font-medium transition-all flex-1",
              active ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            )}>
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Quick picks ─────────────────────────────────────────────

export function QuickPicks({ mode, value, onChange }: { mode: DiscountMode; value: number; onChange: (v: number) => void }) {
  const presets = mode === "percentage" ? [5, 10, 15, 20, 25, 50] : [50, 100, 200, 500, 1000];
  return (
    <div className="flex flex-wrap gap-1.5">
      {presets.map((p) => (
        <button key={p} type="button" onClick={() => onChange(p)}
          className={cn(
            "px-3 py-1.5 rounded text-xs font-medium transition-colors tabular-nums",
            value === p ? "bg-primary text-primary-foreground" : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
          )}>
          {mode === "percentage" ? `${p}%` : `฿${p.toLocaleString()}`}
        </button>
      ))}
    </div>
  );
}

// ── Price calculator ────────────────────────────────────────

export function PriceCalculator({ mode, value }: { mode: DiscountMode; value: number }) {
  const originalPrice = 1990;
  const discounted = useMemo(() => {
    if (!mode || value === 0) return originalPrice;
    if (mode === "percentage") return originalPrice * (1 - value / 100);
    if (mode === "amount") return originalPrice - value;
    if (mode === "rate") return value;
    return originalPrice;
  }, [mode, value]);

  const savings = originalPrice - Math.max(0, discounted);
  if (value === 0 || !mode) return null;

  return (
    <div className="flex items-center justify-between rounded px-4 py-3 bg-primary/[0.05]">
      <div className="flex items-center gap-3">
        <div className="text-center">
          <p className="text-xs text-muted-foreground uppercase">Before</p>
          <p className="text-sm text-muted-foreground line-through tabular-nums">฿{originalPrice.toLocaleString()}</p>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/50" />
        <div className="text-center">
          <p className="text-xs text-muted-foreground uppercase">After</p>
          <p className="text-sm font-semibold tabular-nums text-primary">฿{Math.max(0, Math.round(discounted)).toLocaleString()}</p>
        </div>
      </div>
      <div className="px-2.5 py-1 rounded text-xs font-semibold text-primary-foreground bg-primary">
        Save ฿{Math.round(savings).toLocaleString()}
      </div>
    </div>
  );
}

// ── Scope selector ──────────────────────────────────────────

export function ScopeSelector({ value, onChange, lang }: { value: string; onChange: (v: string) => void; lang: string }) {
  const options = [
    { value: "Transaction", label: { en: "Whole cart", th: "ทั้งตะกร้า" }, icon: Sparkles, desc: { en: "Entire order", th: "ทั้งออเดอร์" } },
    { value: "Item Code", label: { en: "Specific item", th: "สินค้าเฉพาะ" }, icon: Package, desc: { en: "One product", th: "สินค้าตัวเดียว" } },
    { value: "Item Group", label: { en: "Category", th: "หมวดหมู่" }, icon: Tag, desc: { en: "Product group", th: "กลุ่มสินค้า" } },
    { value: "Brand", label: { en: "Brand", th: "แบรนด์" }, icon: Star, desc: { en: "By brand", th: "ตามแบรนด์" } },
  ];
  return (
    <div>
      {options.map((opt, i) => {
        const active = value === opt.value;
        const Icon = opt.icon;
        return (
          <div key={opt.value}>
            {i > 0 && <div className="border-t border-border/50 mx-4" />}
            <button type="button" onClick={() => onChange(opt.value)}
              className="flex items-center gap-3 w-full px-4 py-3.5 text-left transition-colors hover:bg-muted/30">
              <div className={cn("w-8 h-8 rounded flex items-center justify-center transition-colors", active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{lang === "th" ? opt.label.th : opt.label.en}</p>
                <p className="text-xs text-muted-foreground">{lang === "th" ? opt.desc.th : opt.desc.en}</p>
              </div>
              <div className={cn("w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors", active ? "border-primary bg-primary" : "border-muted-foreground/30")}>
                {active && <Check className="w-3 h-3 text-primary-foreground" />}
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── Toggle row (label + description + switch) ────────────────

export function ToggleRow({ icon: Icon, title, desc, checked, onChange }: {
  icon?: typeof Package;
  title: string;
  desc?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className={cn("flex items-center justify-between rounded p-4 transition-colors", checked ? "bg-primary/[0.06]" : "bg-muted/30")}>
      <div className="flex items-center gap-3">
        {Icon && (
          <div className={cn("w-10 h-10 rounded flex items-center justify-center shrink-0", checked ? "bg-primary text-primary-foreground" : "bg-muted")}>
            <Icon className={cn("w-5 h-5", !checked && "text-muted-foreground")} />
          </div>
        )}
        <div>
          <p className="text-sm font-medium">{title}</p>
          {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
